import './style.css';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import calenderIcon from '../../../assets/icons/calendar.svg';
import { IoMdClose } from "react-icons/io";
import useOutsideClick from '../../../hooks/useOutsideClick';

function parseYearFromValue(value) {
  if (!value) return null;
  const strict = moment(value, 'MM-DD-YYYY', true);
  if (strict.isValid()) return strict.year();
  const loose = moment(value);
  if (loose.isValid()) return loose.year();
  return null;
}

const YearOnlyPicker = ({
  value,
  onChange,
  placeholder,
  label,
  error,
  className,
  inputClassName,
  clearable = false,
  disabled = false,
  floatingLabel = true,
}) => {
  const currentYear = new Date().getFullYear();
  const startYear = 1990;
  const endYear = currentYear;
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const initialYear = parseYearFromValue(value) ?? currentYear;
  const [yearVal, setYearVal] = useState(initialYear);
  const [showBox, setShowBox] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    const y = parseYearFromValue(value);
    if (y != null) setYearVal(y);
  }, [value]);

  const handleClose = () => setShowBox(false);
  useOutsideClick(popoverRef, handleClose);

  const displayValue = value
    ? (moment(value, 'MM-DD-YYYY', true).isValid()
      ? value
      : moment(value).format('MM-DD-YYYY'))
    : '';

  const handleYearClick = (year, e) => {
    e.stopPropagation();
    setYearVal(year);
    const normalized = moment({ year, month: 0, date: 1 }).format('MM-DD-YYYY');
    onChange?.(normalized);
    setShowBox(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const toggleBox = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setShowBox((o) => !o);
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        onClick={toggleBox}
      >
        <input
          type="text"
          readOnly
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          className={inputClassName}
          style={{ border: '1px solid #999' }}
        />
      </div>

      <div
        className={`absolute right-3 flex items-center gap-2 ${
          floatingLabel ? 'top-[25px]' : 'top-1/2 -translate-y-1/2'
        }`}
      >
        {clearable && value && !disabled && (
          <IoMdClose
            color='#999'
            onClick={handleClear}
            className='cursor-pointer'
            size={18}
          />
        )}
        <img
          src={calenderIcon}
          alt="calenderIcon"
          className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={toggleBox}
        />
      </div>

      {floatingLabel && label ? (
        <label className="absolute top-[2px] left-2 px-1 text-center text-[12px] bg-[#fff] startDate">
          {label}
        </label>
      ) : null}

      {error && <div className="date-error">{error}</div>}

      {showBox && !disabled && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-[calc(100%-8px)] z-50 mt-1 w-[min(100%,280px)] max-h-[280px] overflow-hidden rounded-[10px] border border-black bg-white shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-[#999] bg-white py-3 text-center font-bold">
            <p className="text-[16px]">
              Birth year{yearVal != null ? ` — ${yearVal}` : ''}
            </p>
          </div>
          <div className="months_text max-h-[220px] overflow-y-auto">
            <div className="flex flex-col pb-3">
              {years
                .slice()
                .reverse()
                .map((y) => (
                  <button
                    type="button"
                    key={y}
                    className={`mt-4 w-full cursor-pointer border-0 bg-transparent py-2 text-center font-montserrat text-sm text-black ${y === yearVal ? 'bg-[#DEE6F0] font-bold' : ''
                      }`}
                    onClick={(e) => handleYearClick(y, e)}
                  >
                    {y}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomDatePicker = ({
  value,
  onChange,
  isValidDate,
  allowCurrentAndPreviousYear = false,
  placeholder,
  label,
  error,
  className,
  inputClassName,
  clearable = false,
  disabled = false,
  yearOnly = false,
  /** When false, omit the inset border label; use an external label for filter/toolbars. */
  floatingLabel = true,
  inputId,
}) => {
  const inputRef = useRef(null);
  const datePickerRef = useRef(null);

  const defaultIsValidDate = (currentDate) => {
    const allowedDate = moment().subtract(1, 'years').startOf('year');
    return currentDate.isSameOrAfter(allowedDate);
  };

  const finalIsValidDate = allowCurrentAndPreviousYear ? defaultIsValidDate : isValidDate;

  useEffect(() => {
    if (!value && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [value]);

  const handleChange = (date) => {
    if (typeof onChange === 'function') {
      if (moment.isMoment(date) && date.isValid()) {
        onChange(date.format('MM-DD-YYYY'));
      } else {
        onChange(null);
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    if (datePickerRef.current) {
      datePickerRef.current.setState({ inputValue: '' });
    }
  };

  if (yearOnly) {
    return (
      <YearOnlyPicker
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        label={label}
        error={error}
        className={className}
        inputClassName={inputClassName}
        clearable={clearable}
        disabled={disabled}
        floatingLabel={floatingLabel}
      />
    );
  }

  const ariaLabel = label || placeholder || 'Select date';

  return (
    <div className={`relative min-w-0 max-w-full ${className}`}>
      <DatePicker
        key={value ? `date-${value}` : 'date-empty'}
        timeFormat={false}
        value={value ? moment(value, 'MM-DD-YYYY') : null}
        isValidDate={finalIsValidDate}
        closeOnSelect={true}
        dateFormat="MM-DD-YYYY"
        className="font-montserrat text-sm w-full min-w-0 max-w-full"
        inputProps={{
          ref: inputRef,
          id: inputId,
          'aria-label': ariaLabel,
          placeholder: placeholder,
          readOnly: true,
          disabled: disabled,
          className: inputClassName,
          style: { border: '1px solid #999' },
        }}
        onChange={handleChange}
        ref={datePickerRef}
      />

      <div
        className={`absolute right-3 flex items-center gap-2 ${
          floatingLabel ? 'top-[25px]' : 'top-1/2 -translate-y-1/2'
        }`}
      >
        {clearable && value && (
          <IoMdClose
            color='#999'
            onClick={handleClear}
            className='cursor-pointer'
            size={18}
          />
        )}
        <img
          src={calenderIcon}
          alt="calenderIcon"
          className={`${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => inputRef.current?.focus()}
        />
      </div>

      {floatingLabel && label ? (
        <label className="absolute top-[2px] left-2 px-1 text-center text-[12px] bg-[#fff] startDate">
          {label}
        </label>
      ) : null}

      {error && <div className="date-error">{error}</div>}
    </div>
  );
};

export default CustomDatePicker;
